/**
 * I SIMBOLI CANONICI, e come si ritrovano sul server di un broker.
 *
 * ---------------------------------------------------------------------------
 * LA LISTA STA QUI, E IN NESSUN ALTRO POSTO
 * ---------------------------------------------------------------------------
 *
 * «Canonico» e' il nome con cui questo gestionale chiama uno strumento; il nome
 * sul server e' un'altra cosa e cambia da broker a broker — `XAUUSD`, `XAUUSD-E`,
 * `XAUUSD.r`. La mappatura fra i due e' `SymbolMapping`.
 *
 * La lista sta in `shared` perche' la leggono tre posti che non possono
 * importarsi a vicenda: la mappatura automatica alla connessione, il comando
 * che la rifa' sui server gia' esistenti, e la pagina Server. Una seconda copia
 * sarebbe una seconda idea di quali strumenti esistono, e la seconda resterebbe
 * indietro.
 *
 * ---------------------------------------------------------------------------
 * LA RICERCA DEL NOME: PRIMA L'ESATTO, POI IL SUFFISSO, E BASTA
 * ---------------------------------------------------------------------------
 *
 * Due passi, in quest'ordine:
 *
 *   1. **il nome esatto**. Se il server ha `XAUUSD`, quello e' il simbolo: non
 *      si guarda nemmeno se esistono `XAUUSD.m` o `XAUUSDm`. E' il passo che
 *      rende la ricerca decidibile sui broker che offrono anche i micro-lotti;
 *   2. **canonico + separatore + suffisso**: `XAUUSD-E`, `XAUUSD.r`,
 *      `XAUUSD_x`, `XAUUSD#`… con uno di `SUFFIX_SEPARATORS` subito dopo il
 *      canonico. Un suffisso attaccato (`XAUUSDm`, `XAUUSD247.s`) NON e' un
 *      candidato automatico: e' spesso un altro strumento — micro-lotti,
 *      un contratto diverso — e resta da scegliere a mano. Fino al punto 25
 *      (01/10) bastava che il nome, tolti i separatori, cominciasse col
 *      canonico: `XAUUSDm` accanto a `XAUUSD-E` rendeva ambiguo l'oro di FPG.
 *
 * La lista della scelta MANUALE resta larga (`looksLikeCanonical`): li' una
 * persona vede tutti i nomi simili, ciascuno coi suoi numeri e i suoi avvisi.
 *
 * ---------------------------------------------------------------------------
 * IL CONTRATTO ATTESO: IL SUFFISSO SI SCEGLIE DA SOLO SOLO DOVE LO SI CONOSCE
 * ---------------------------------------------------------------------------
 *
 * Un nome per suffisso si mappa in automatico solo se il canonico ha un
 * contratto atteso (`EXPECTED_CONTRACT_SIZE`), e solo se il candidato lo
 * rispetta. Gli altri canonici, per suffisso, si mappano a mano; il nome
 * esatto vale sempre, ma con un contratto diverso da quello atteso si rifiuta.
 *
 * **Nessun alias.** `GOLD` e' oro per una persona, non per questa funzione: un
 * dizionario di sinonimi sarebbe una tabella di ipotesi, e un'ipotesi sbagliata
 * qui manda a mercato lo strumento sbagliato. I simboli che non si ritrovano
 * per nome si mappano a mano, con una persona che sceglie (DECISIONE 27).
 *
 * **Piu' di un candidato non e' una scelta: e' un'ambiguita'**, e si dichiara
 * invece di essere risolta col primo in ordine alfabetico. Fra `XAUUSD.m` e
 * `XAUUSDm` la differenza e' il contract size — 10 contro 100 — cioe' un errore
 * di dieci volte su ogni importo, in silenzio.
 */
/**
 * Gli strumenti su cui questo gestionale opera.
 *
 * Non e' «tutto quello che il broker offre»: e' l'elenco di quello che una
 * coppia puo' usare, e un simbolo si potra' scegliere in un'operazione solo se
 * e' mappato su TUTT'E DUE i server della coppia.
 */
export const CANONICAL_SYMBOLS = [
    "EURUSD",
    "GBPUSD",
    "USDJPY",
    "XAUUSD",
    "USDCAD",
    "EURJPY",
    "GBPJPY",
    "USDCHF",
    "BTCUSD",
];
export function isCanonicalSymbol(name) {
    return CANONICAL_SYMBOLS.includes(name.trim().toUpperCase());
}
/**
 * IL CONTRATTO ATTESO DI UN CANONICO, e da dove lo sappiamo.
 *
 *   - `MEASURED`: letto su server veri. XAUUSD, 100 once: e' anche il numero su
 *     cui `symbolSpecWarnings` controlla il valore punto;
 *   - `DECLARED`: il lotto standard del forex, 100.000 unita'. E' una
 *     dichiarazione, non una misura: serve a ESCLUDERE (un candidato con un
 *     altro contratto non si sceglie da solo e resta manuale), mai a decidere
 *     un numero che entra in colonna — quelli si rileggono sempre dal gateway.
 *
 * BTCUSD non c'e': il contratto cambia da broker a broker, e per suffisso si
 * mappa a mano.
 */
export const EXPECTED_CONTRACT_SIZE = {
    XAUUSD: { size: 100, origin: "MEASURED" },
    EURUSD: { size: 100_000, origin: "DECLARED" },
    GBPUSD: { size: 100_000, origin: "DECLARED" },
    USDJPY: { size: 100_000, origin: "DECLARED" },
    USDCAD: { size: 100_000, origin: "DECLARED" },
    EURJPY: { size: 100_000, origin: "DECLARED" },
    GBPJPY: { size: 100_000, origin: "DECLARED" },
    USDCHF: { size: 100_000, origin: "DECLARED" },
};
/** Il contratto atteso, o `null` se per questo canonico non lo conosciamo. */
export function expectedContractSize(canonical) {
    return EXPECTED_CONTRACT_SIZE[canonical.trim().toUpperCase()]?.size ?? null;
}
/**
 * Il contratto letto rispetta quello atteso? `null` = non si sa (canonico
 * senza contratto atteso). Il confronto e' sul `contractSize` del gateway, non
 * sul valore punto: quello e' nella valuta di deposito, e su una coppia in JPY
 * sarebbe lontano dal contratto per il cambio, non per lo strumento.
 * Tolleranza 0,5%, la stessa di `SYMBOL_CONTRACT_SIZE_MISMATCH`.
 */
export function contractMatches(canonical, contractSize) {
    const atteso = expectedContractSize(canonical);
    if (atteso === null)
        return null;
    return Number.isFinite(contractSize) && contractSize > 0 && Math.abs(contractSize - atteso) / atteso <= 0.005;
}
/** I separatori ammessi fra il canonico e il suffisso, per la scelta automatica. */
export const SUFFIX_SEPARATORS = [".", "_", "-", "#"];
/**
 * `XAUUSD-E` si', `XAUUSD.s` si', `XAUUSDm` no, `XAUUSD247.s` no: dopo il
 * canonico deve venire SUBITO un separatore. Il suffisso puo' essere vuoto
 * solo per `#` (`XAUUSD#` e' un nome che esiste); per gli altri separatori un
 * nome che finisce col separatore non e' un suffisso, e' un refuso.
 */
export function hasSeparatedSuffix(candidate, canonical) {
    const nome = candidate.trim().toUpperCase();
    const base = canonical.trim().toUpperCase();
    if (!nome.startsWith(base) || nome.length === base.length)
        return false;
    const sep = nome[base.length];
    if (!SUFFIX_SEPARATORS.includes(sep))
        return false;
    return sep === "#" || nome.length > base.length + 1;
}
/** Il nome senza separatori e in maiuscolo: `XAUUSD-E` -> `XAUUSDE`. */
export const normalizeSymbolName = (name) => name.toUpperCase().replace(/[^A-Z0-9]/g, "");
/**
 * Due nomi si somigliano se, tolti i separatori, il candidato comincia col
 * canonico. `XAUUSD` trova `XAUUSD`, `XAUUSD-E`, `XAUUSD.m`, `XAUUSDm`; non
 * trova `XAGUSD` ne' `EURUSD`.
 *
 * E' la lista della scelta MANUALE, larga di proposito: la scelta automatica
 * usa `hasSeparatedSuffix`, che e' piu' stretta.
 *
 * La somiglianza **restringe**, non decide: quando i candidati sono piu' d'uno
 * sceglie una persona.
 */
export const looksLikeCanonical = (candidate, canonical) => normalizeSymbolName(candidate).startsWith(normalizeSymbolName(canonical));
/**
 * Il nome di un canonico sul server, fra quelli che il server dichiara.
 *
 * `names` e' la lista COMPLETA dei simboli del server (`symbolList`): il
 * filtro si fa qui, perche' e' qui che vive la regola.
 */
export function matchCanonical(canonical, names) {
    const target = canonical.trim().toUpperCase();
    // 1 — Il nome esatto vince su tutto, e si restituisce come lo scrive il
    //     server: e' quello che andra' nell'ordine.
    const esatto = names.find((n) => n.trim().toUpperCase() === target);
    if (esatto)
        return { kind: "EXACT", brokerSymbol: esatto };
    // 2 — Canonico + separatore + suffisso. Ordinati per rendere il messaggio
    //     ripetibile. Il contratto non si guarda qui: serve la lettura dei
    //     parametri, e la fa `lib/symbol-automap.ts`.
    const simili = names.filter((n) => hasSeparatedSuffix(n, target)).sort();
    if (simili.length === 1)
        return { kind: "SUFFIX", brokerSymbol: simili[0] };
    if (simili.length === 0)
        return { kind: "NOT_FOUND" };
    return { kind: "AMBIGUOUS", candidates: simili };
}
//# sourceMappingURL=canonical-symbols.js.map